import {
    AuthApi,
    BankingApi,
    CoreFetch,
    IntegrationApi, IntegrationHl7VendorType, IntegrationSftpVendorType,
    InvitationApi,
    InvitationStatusType,
    MeApi,
    MeTwoFactorResponse,
    ProviderAccountRole,
    ProviderIntegrationApi,
    ProviderIntegrationHl7, ProviderIntegrationSftp,
    ProviderIntegrationWebhooks,
    ProviderIntegrationWebhooksFromJSON,
    ProviderInvitation,
    ProviderPlaidItemInfo,
    ProviderUser,
    Role,
    TwoFactorType,
    UserApi,
} from '@enter/core';
import _ from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { IFeatures } from './root.store';
import { SettingsGenerator } from './settings.generator';
import { SettingsService } from './settings.service';
import { equalsFilter } from './util';

export class SettingsStore {
    @observable public usersLoaded: boolean = false;
    @observable public rolesLoaded: boolean = false;
    @observable public users: ProviderUser[] | undefined;
    @observable public invitations: ProviderInvitation[] | undefined;
    @observable public roles: Role[] | undefined;

    @observable public integrationApis: ProviderIntegrationApi[] | undefined;

    @observable public integrationHl7s: ProviderIntegrationHl7[] | undefined;

    @observable public integrationWebhooks: ProviderIntegrationWebhooks | undefined;

    @observable public singleSignOnURL: string | undefined;

    @observable public integrationSftp: ProviderIntegrationSftp | undefined;

    private features: IFeatures;
    private settingsGenerator: SettingsGenerator;
    private settingsService: SettingsService = new SettingsService();

    constructor(features: IFeatures, settingsGenerator: SettingsGenerator) {
        makeObservable(this);
        this.features = features;
        this.settingsGenerator = settingsGenerator;
    }

    public getCurrentUser(userId: string | undefined): ProviderUser | undefined {
        if (this.features.isDemo) {
            return _.first(this.users);
        }

        return _.find(this.users, { userId });
    }

    @action
    public async initSettings(): Promise<void> {
        await Promise.all([this.fetchRoles(), this.fetchUsers(), this.fetchInvitations(), this.fetchIntegrationApis(), this.fetchIntegrationHl7s(), this.fetchIntegrationSftp()]);
    }

    @action
    public async fetchIntegrationWebhooksIfNeeded(): Promise<void> {
        let integrationWebhooks:ProviderIntegrationWebhooks|undefined = undefined;

        if(!this.integrationWebhooks) {
            integrationWebhooks = await (this.features.isDemo
                ? ProviderIntegrationWebhooksFromJSON('{}')
                : this.settingsService.getProviderIntegrationWebhooksUrl());
        }

        runInAction(() => {
            this.integrationWebhooks = integrationWebhooks;
        });
    }

    @action
    public async fetchIntegrationApis(): Promise<void> {
        const integrationApis = await (this.features.isDemo
            ? this.settingsGenerator.listProviderIntegrationApis()
            : this.settingsService.listProviderIntegrationApis());

        runInAction(() => {
            this.integrationApis = integrationApis;
        });
    }

    @action
    public async fetchIntegrationHl7s(): Promise<void> {
        const integrationApis = await (this.features.isDemo
            ? this.settingsGenerator.listProviderIntegrationHl7s()
            : this.settingsService.listProviderIntegrationHl7s());

        runInAction(() => {
            this.integrationHl7s = integrationApis;
        });
    }

    @action
    public async fetchIntegrationSftp(): Promise<void> {
        const integrationSftps = await (
          this.features.isDemo ? this.settingsGenerator.listProviderIntegrationSftps()
          : this.settingsService.listProviderIntegrationSftps()
        );

        runInAction(() => {
           this.integrationSftp = integrationSftps.length > 0 ? integrationSftps[0] : undefined;
        });
    }

    @action
    public async fetchRoles(): Promise<void> {
        const roles = await (this.features.isDemo
            ? this.settingsGenerator.listProviderRoles()
            : this.settingsService.listProviderRoles());

        runInAction(() => {
            this.roles = roles;
            this.rolesLoaded = true;
        });
    }

    @action
    public async fetchUsers(): Promise<void> {
        const users = await (this.features.isDemo
            ? this.settingsGenerator.listProviderUsers()
            : this.settingsService.listProviderUsers());

        runInAction(() => {
            this.users = users;
            this.usersLoaded = true;
        });
    }

    @action
    public async fetchInvitations(): Promise<void> {
        const invitations = await CoreFetch<InvitationApi>(InvitationApi).listProviderInvitations({
            filterStatus: equalsFilter(InvitationStatusType.SENT),
        });
        runInAction(() => {
            this.invitations = invitations;
        });
    }

    @action
    // @ts-expect-error - banking is deprecated
    public async fetchBankingAccounts(): Promise<ProviderBankingAccount[]> {
        return this.features.isDemo
            ? this.settingsGenerator.listProviderBankingAccounts()
            : this.settingsService.listProviderBankingAccounts();
    }

    @action
    public async fetchPlaidBankingAccounts(): Promise<ProviderPlaidItemInfo[]> {
        return this.features.isDemo
            ? this.settingsGenerator.listPlaidItems()
            : this.settingsService.listPlaidItems();
    }

    @action
    public async changeUserPassword(currentPassword: string, newPassword: string): Promise<void> {
        await CoreFetch<AuthApi>(AuthApi).providerResetPassword({
            resetPasswordProviderRequest: {
                currentPassword,
                newPassword,
            },
        });
    }

    @action
    public async changeUserName(user: ProviderUser, firstName: string, lastName: string): Promise<void> {
        await CoreFetch<MeApi>(MeApi).mePostPersonalInfoName({
            meChangeNameRequest: { firstName, lastName },
        });
        runInAction(() => {
            // update local user instead of re-requesting
            const u = _.find(this.users, user);
            if (u) {
                u.first = firstName;
                u.last = lastName;
            }
        });
    }

    @action
    public async changeUserRole(user: ProviderUser, roles: ProviderAccountRole[]): Promise<void> {
        await CoreFetch<UserApi>(UserApi).updateProviderUser({
            id: user.userId as string,
            providerUpdateUserPayload: { roles },
        });
        runInAction(() => {
            // update local user instead of re-requesting
            const u = _.find(this.users, user);
            if (u) {
                u.roles = roles;
            }

            if (roles.length === 0) {
                this.users = _.without(this.users, user);
            }
        });
    }

    @action
    public async removeUser(user: ProviderUser): Promise<void> {
        await CoreFetch<UserApi>(UserApi).disableProviderUser({
            id: user.userId as string,
        });
        runInAction(() => {
            this.users = _.without(this.users, user);
        });
    }

    @action
    public async resendInvitation(invitation: ProviderInvitation): Promise<void> {
        const res = await CoreFetch<InvitationApi>(InvitationApi).createProviderInvitation({
            providerCreateInvitationPayload: {
                email: invitation.email,
                roles: [
                    {
                        accountId: invitation.accountId,
                        roleId: invitation.roleId,
                    },
                ],
            },
        });
        await CoreFetch<InvitationApi>(InvitationApi).deleteProviderInvitation({
            id: invitation.invitationId as string,
        });
        const removedInvitation = _.find(this.invitations, el => el.invitationId === invitation.invitationId);

        runInAction(() => {
            this.invitations = removedInvitation
                ? _.union(_.without(this.invitations, removedInvitation), res.invitations)
                : _.union(this.invitations, res.invitations);
        });
    }

    @action
    public async removeInvitation(invitation: ProviderInvitation): Promise<void> {
        await CoreFetch<InvitationApi>(InvitationApi).deleteProviderInvitation({
            id: invitation.invitationId as string,
        });
        runInAction(() => {
            this.invitations = _.without(this.invitations, invitation);
        });
    }

    @action
    public async inviteMember(email: string, roles: ProviderAccountRole[]): Promise<void> {
        const res = await CoreFetch<InvitationApi>(InvitationApi).createProviderInvitation({
            providerCreateInvitationPayload: {
                email,
                roles,
            },
        });
        runInAction(() => {
            this.invitations = _.union(this.invitations, res.invitations);
        });
    }

    @action
    public async createAPIKey(name: string): Promise<void> {
        const res = await CoreFetch<IntegrationApi>(IntegrationApi).postProviderIntegrationApi({providerCreateIntegrationApiPayload: {name}});
        runInAction(() => {
           this.integrationApis = _.union(this.integrationApis, [res]);
        });
    }

    @action
    public async createHl7(vendorType: IntegrationHl7VendorType): Promise<void> {
        const res = await CoreFetch<IntegrationApi>(IntegrationApi).postProviderIntegrationHl7({providerCreateIntegrationHl7Payload: {vendorType}});
        runInAction(() => {
            this.integrationHl7s = _.union(this.integrationHl7s, [res]);
        });
    }

    @action
    public async createSingleSignOnURL(domains: string[]): Promise<void> {
        const res = await CoreFetch<IntegrationApi>(IntegrationApi).getProviderIntegrationSso();
        runInAction(() => {
            this.singleSignOnURL = res.url;
        });
    }

    @action
    public async saveSftpIntegration(vendorType: IntegrationSftpVendorType, publicKey: string, previousId?: string): Promise<void> {
        if(previousId) {
            await CoreFetch<IntegrationApi>(IntegrationApi).disableProviderSftpIntegration({id: previousId});
        }
        const res = await CoreFetch<IntegrationApi>(IntegrationApi).postProviderIntegrationSftp({providerCreateIntegrationSftpPayload: {vendorType, publicKey}});
        runInAction(() => {
            this.integrationSftp = res;
        });
    }

    @action
    public async deleteAPIKey(id: string): Promise<void> {
        await CoreFetch<IntegrationApi>(IntegrationApi).disableProviderApiIntegration({
            id
        });
        const removedIntegration = _.find(this.integrationApis, el => el.id === id);
        runInAction(() => {
            this.integrationApis = removedIntegration
                ? _.without(this.integrationApis, removedIntegration)
                : this.integrationApis;
        });
    }

    @action
    public async deleteHl7(id: string): Promise<void> {
        await CoreFetch<IntegrationApi>(IntegrationApi).disableProviderHl7Integration({
            id
        });
        const removedIntegration = _.find(this.integrationHl7s, el => el.id === id);
        runInAction(() => {
            this.integrationHl7s = removedIntegration
                ? _.without(this.integrationHl7s, removedIntegration)
                : this.integrationHl7s;
        });
    }

    @action
    public async initiateTwoFactorEnable(): Promise<string | undefined> {
        const response = await CoreFetch<MeApi>(MeApi).mePostPersonalTwoFactorPreference({
            mePersonalTwoFactorRequest: {
                enabled: true,
            },
        });
        return response.phoneVerificationId;
    }

    @action
    public async verifyTwoFactorEnable(phoneVerificationId: string, code: string): Promise<void> {
        await CoreFetch<MeApi>(MeApi).mePostPersonalTwoFactorPreferenceVerificationVerification({
            mePersonalTwoFactorVerificationRequest: {
                phoneVerificationId,
                code,
            },
        });
    }

    @action
    public async initiateTwoFactorChange(twoFactorType: TwoFactorType): Promise<MeTwoFactorResponse | undefined> {
        return await CoreFetch<MeApi>(MeApi).mePostPersonalTwoFactorPreference({
            mePersonalTwoFactorRequest: {
                enabled: true,
                twoFactorType
            },
        });
    }

    @action
    public async verifyTwoFactorChange(phoneVerificationId: string, code: string): Promise<void> {
        await CoreFetch<MeApi>(MeApi).mePostPersonalTwoFactorPreferenceVerificationVerification({
            mePersonalTwoFactorVerificationRequest: {
                phoneVerificationId,
                code
            },
        });
    }

    @action
    public async setChargeAccount(id: string): Promise<void> {
        // @ts-expect-error - banking is deprecated
        await CoreFetch<BankingApi>(BankingApi).postProviderBankingAccountChargeBankingAccount({
            id,
        });
    }

    @action
    public async setDepositAccount(id: string): Promise<void> {
        // @ts-expect-error - banking is deprecated
        await CoreFetch<BankingApi>(BankingApi).postProviderBankingAccountDepositBankingAccount({
            id,
        });
    }

    @action
    public async setMonitoringAccount(id: string): Promise<void> {
        // @ts-expect-error - banking is deprecated
        await CoreFetch<BankingApi>(BankingApi).postProviderBankingAccountMonitorBankingAccount({
            id,
        });
    }

    @action
    public async getPlaidLinkToken(): Promise<string | undefined> {
        if (this.features.isDemo) {
            return 'link-sandbox-demo-token';
        }
        const response = await this.settingsService.getPlaidLinkToken();
        return response.token;
    }

    @action
    public async connectPlaidAccount(publicToken: string, institutionId: string): Promise<void> {
        if (this.features.isDemo) {
            return;
        }
        await this.settingsService.connectPlaidAccount(publicToken, institutionId);
    }
}
